/**
 * Newsletter Signup — Variant A: Terminal Sidebar Widget
 *
 * Dark terminal aesthetic. Used in the post sidebar.
 * Fits the Veil Framework "Blueprint Terminal" design system.
 *
 * Classes: nl-sw-* (sidebar widget namespace)
 * Site: veil-framework
 */
import React, { useState } from "react";

export default function NewsletterSidebar({ sourceUrl }) {
  const [phase, setPhase] = useState("idle"); // idle | loading | done | err
  const [errText, setErrText] = useState("");

  async function handleSubscribe(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // Honeypot — bot filled hidden field, bail silently
    if (data.get("company")) return;

    setPhase("loading");
    setErrText("");

    try {
      const res = await fetch("/api/subscribe", { method: "POST", body: data });
      const json = await res.json();
      if (json.success) {
        setPhase("done");
        form.reset();
      } else {
        setPhase("err");
        setErrText(json.error || "Something went wrong.");
      }
    } catch {
      setPhase("err");
      setErrText("Network error — please try again.");
    }
  }

  return (
    <aside className="nl-sw-wrap" aria-label="Newsletter signup">
      <div className="nl-sw-header">
        <span className="nl-sw-dot nl-sw-dot-r" aria-hidden="true" />
        <span className="nl-sw-dot nl-sw-dot-a" aria-hidden="true" />
        <span className="nl-sw-dot nl-sw-dot-g" aria-hidden="true" />
        <span className="nl-sw-title">intel_feed.sh</span>
      </div>

      <div className="nl-sw-body">
        <p className="nl-sw-prompt">
          <span className="nl-sw-caret" aria-hidden="true">$</span>{" "}
          subscribe --to evasion-intel
        </p>

        <h3 className="nl-sw-heading">Stay ahead of the curve</h3>
        <p className="nl-sw-desc">
          AV bypass techniques, detection gaps, and lab-tested research — delivered
          before the mainstream catches up.
        </p>

        {phase === "done" ? (
          <div className="nl-sw-success" role="status">
            <span className="nl-sw-check" aria-hidden="true">✓</span>
            <div>
              <strong>Subscribed.</strong>
              <br />
              <span className="nl-sw-success-sub">Confirm your email to activate.</span>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubscribe} className="nl-sw-form" noValidate>
            {/* Hidden: site identifier */}
            <input type="hidden" name="site" value="veil-framework" />
            {/* Hidden: source page */}
            <input type="hidden" name="source_url" value={sourceUrl || ""} />
            {/* Honeypot: never shown to real users */}
            <input
              type="text"
              name="company"
              tabIndex="-1"
              aria-hidden="true"
              className="nl-sw-hp"
              autoComplete="off"
            />

            <label htmlFor="nl-sw-name" className="nl-sw-label">
              Your name
            </label>
            <input
              id="nl-sw-name"
              type="text"
              name="name"
              placeholder="alias or handle"
              required
              autoComplete="given-name"
              className="nl-sw-input"
              disabled={phase === "loading"}
            />

            <label htmlFor="nl-sw-email" className="nl-sw-label">
              Email address
            </label>
            <input
              id="nl-sw-email"
              type="email"
              name="email"
              placeholder="operator@domain.com"
              required
              autoComplete="email"
              className="nl-sw-input"
              disabled={phase === "loading"}
            />

            {phase === "err" && (
              <p className="nl-sw-error" role="alert">{errText}</p>
            )}

            <button
              type="submit"
              className="nl-sw-btn"
              disabled={phase === "loading"}
            >
              {phase === "loading" ? "Connecting…" : "Subscribe to intel →"}
            </button>

            <p className="nl-sw-legal">
              No spam. No vendor fluff. Unsubscribe any time.
            </p>
          </form>
        )}
      </div>
    </aside>
  );
}
